import { useRef, useState } from "react";
import api from "@/lib/api";
import { mediaUrl } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Upload, X, Image as ImageIcon, Loader2 } from "lucide-react";
import { toast } from "sonner";

export function ImageUpload({ value, onChange, label = "Image", testid = "image-upload", aspect = "aspect-video" }) {
  const inputRef = useRef(null);
  const [busy, setBusy] = useState(false);

  const onFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!f.type.startsWith("image/")) { toast.error("Please choose an image file."); return; }
    if (f.size > 5 * 1024 * 1024) { toast.error("Image must be 5 MB or smaller."); return; }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const { data } = await api.post("/upload/image", fd, { headers: { "Content-Type": "multipart/form-data" } });
      onChange(data.url);
      toast.success("Image uploaded");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload failed, please try again.");
    } finally {
      setBusy(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  return (
    <div data-testid={testid}>
      {label && <p className="text-sm font-medium mb-1.5">{label}</p>}
      <div className={`relative ${aspect} w-full rounded-sm border border-border bg-muted overflow-hidden flex items-center justify-center`}>
        {value ? (
          <img src={mediaUrl(value)} alt="preview" className="w-full h-full object-cover" data-testid={`${testid}-preview`} />
        ) : (
          <div className="text-muted-foreground flex flex-col items-center text-xs"><ImageIcon className="w-6 h-6 mb-1" />No image yet</div>
        )}
        {busy && <div className="absolute inset-0 bg-black/40 flex items-center justify-center"><Loader2 className="w-6 h-6 text-white animate-spin" /></div>}
      </div>
      <div className="flex gap-2 mt-2">
        <Button type="button" variant="outline" size="sm" className="rounded-sm" onClick={() => inputRef.current?.click()} disabled={busy} data-testid={`${testid}-btn`}>
          <Upload className="w-4 h-4 mr-1" />{value ? "Replace image" : "Upload image"}
        </Button>
        {value && <Button type="button" variant="ghost" size="sm" className="rounded-sm" onClick={() => onChange("")} data-testid={`${testid}-remove`}><X className="w-4 h-4" /></Button>}
      </div>
      <input ref={inputRef} type="file" accept="image/*" onChange={onFile} className="hidden" data-testid={`${testid}-input`} />
    </div>
  );
}
